import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SystemStyleObject } from '@mui/system';
import { useRouter } from 'next/router';
import { signIn, useSession } from 'next-auth/react';
import { FC, PropsWithChildren, RefObject, useEffect, useRef } from 'react';

import { Header } from '../../layout/main-layout/header';

import { Sidebar } from '@/layout/main-layout/sidebar';
import { useTelmaPortalStore } from '@/store/telma-portal-store';
import { exportDynamic } from 'utils';

/** One viewport tall and clipped, so `main` is the page's only scroller. */
const shellSx: SystemStyleObject<Theme> = {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'auto minmax(0, 1fr)' },
    gridTemplateRows: 'minmax(0, 1fr)',
    height: '100vh',
    // Mobile browser bars no longer collapse, so size to the visible area.
    '@supports (height: 100dvh)': { height: '100dvh' },
    overflow: 'hidden',
};

const mainSx = (theme: Theme): SystemStyleObject<Theme> => ({
    boxSizing: 'border-box',
    overflowY: 'auto',
    transition: 'all 225ms cubic-bezier(0,0,0.2,1) 0s',
    padding: '1rem 2rem',
    background: theme.palette.surface.canvas,
});

/** Next.js only resets the window on navigation; do the same for `main`. */
const useScrollResetOnNavigation = (scrollerRef: RefObject<HTMLElement>) => {
    const { asPath } = useRouter();
    const path = asPath.split(/[?#]/)[0];

    useEffect(() => {
        scrollerRef.current?.scrollTo({ top: 0 });
    }, [path, scrollerRef]);
};

const Layout: FC<PropsWithChildren> = ({ children }) => {
    const opened = useTelmaPortalStore((state) => state.opened);
    const mainRef = useRef<HTMLElement>(null);
    useScrollResetOnNavigation(mainRef);

    const { data: session } = useSession();

    useEffect(() => {
        if (session?.tokenError === 'RefreshAccessTokenError') {
            signIn('keycloak'); // Force sign in to obtain new tokens
        }
    }, [session]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            {isMobile && (
                <AppBar
                    position='fixed'
                    color='inherit'
                    elevation={0}
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        transition: opened
                            ? theme.transitions.create('width')
                            : 'none',
                    }}
                >
                    <Toolbar>
                        <Header />
                    </Toolbar>
                </AppBar>
            )}
            <Box sx={shellSx}>
                <Sidebar />

                <Box component='main' ref={mainRef} sx={mainSx}>
                    {isMobile && <Toolbar />}
                    {children}
                </Box>
            </Box>
        </>
    );
};

export default exportDynamic(Layout);
